// index.ts

import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { getItem, setItem, removeItem } from "../utils/localStorage";
import { ApiError } from "../types/error"; // Votre type d'erreur

// Créez l'instance de base
const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_BASE_URL, // Remplacez par votre URL de base
  headers: {
    "Content-Type": "application/json",
  },
});

// Variable pour suivre si nous sommes en train de rafraîchir le jeton
let isRefreshing = false;
// Tableau pour stocker les requêtes en attente
let failedQueue: {
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
  config: InternalAxiosRequestConfig;
}[] = [];

// Fonction pour ajouter les requêtes à la file d'attente
const processQueue = (
  error: AxiosError<ApiError> | null,
  token: string | null = null
) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      // Si le jeton est rafraîchi, on met à jour l'en-tête et on rejoue la requête
      prom.config.headers.Authorization = `Bearer ${token}`;
      prom.resolve(apiClient(prom.config));
    }
  });

  failedQueue = [];
};

// **Intercepteur de Réponse**
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    }; // S'il n'y a pas de réponse, c'est une erreur réseau (Timeout, DNS, CORS masqué, etc.)

    //GESTION DES ERREURS RÉSEAU (Globale)
    if (!error.response) {
      console.error("Erreur Réseau sans réponse HTTP:", error.message);

      // Créez une erreur normalisée que vos fonctions catch pourront utiliser
      const networkError: ApiError = {
        error: error.code || "NETWORK_ERROR", // ERR_NETWORK, ETIMEDOUT, etc.
        message:
          error.message === "Network Error"
            ? "Impossible de joindre le serveur. Vérifiez votre connexion ou l'URL de l'API."
            : error.message,
        timestamp: new Date().toISOString(),
      };
      // Rejetez avec une erreur Axios simulée pour conserver le format d'Axios
      // ou enveloppez-la dans une erreur spécifique que vous gérerez dans auth.ts (voir section 2).

      // Pour l'instant, nous la rejetons pour qu'elle soit capturée par votre nouveau helper dans auth.ts
      return Promise.reject({ ...error, response: { data: networkError } });
    }

    //Vérifiez s'il s'agit d'une erreur 401 et si elle n'a pas déjà été rejouée
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      console.log("Error 401");
      // Stockez la requête pour la rejouer plus tard
      originalRequest._retry = true;

      // Récupérez les jetons actuels
      const currentToken = getItem("authToken");
      const refreshToken = getItem("refreshToken"); // **IMPORTANT** : Vous devez stocker le refreshToken lors du login.

      // Si pas de refreshToken, on redirige au login
      if (!refreshToken) {
        // Redirection vers la page de connexion (vous devrez implémenter cette logique)
        // Par exemple: window.location.href = '/login';
        return Promise.reject(error);
      }

      // 2. Tentez de rafraîchir le jeton
      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Créez une nouvelle instance SANS l'intercepteur pour éviter une boucle infinie
          const refreshInstance = axios.create({
            baseURL: apiClient.defaults.baseURL,
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          });

          // Appel à l'API pour rafraîchir le jeton
          const refreshResponse = await refreshInstance.post(
            "/auth/refresh-token"
          );

          console.log("refreshResponse", refreshResponse);

          const newAccessToken = refreshResponse.data.accessToken;
          const newRefreshToken =
            refreshResponse.data.refreshToken || refreshToken; // Le refresh token peut aussi être renouvelé

          // Mettez à jour les jetons
          setItem("authToken", newAccessToken);
          setItem("refreshToken", newRefreshToken);

          // Mettez à jour l'en-tête de la requête originale
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // Videz et exécutez la file d'attente des requêtes échouées
          processQueue(null, newAccessToken);

          isRefreshing = false;

          // 3. Rejouez la requête originale qui a échoué
          return apiClient(originalRequest);
        } catch (refreshError) {
          // Si le rafraîchissement échoue (e.g., refreshToken est invalide/expiré)
          isRefreshing = false;
          processQueue(refreshError as AxiosError<ApiError>); // Rejette toutes les requêtes en attente

          // Déconnexion forcée
          removeItem("authToken");
          removeItem("refreshToken");
          // Redirection vers le login

          return Promise.reject(refreshError);
        }
      }

      // 4. Si un rafraîchissement est déjà en cours, ajoutez la requête à la file d'attente
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject, config: originalRequest });
      });
    }

    return Promise.reject(error);
  }
);

// **Intercepteur de Requête** (pour ajouter l'authToken par défaut)
// Il est souvent plus propre d'utiliser un intercepteur de requête pour ajouter l'Authorization.
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getItem("authToken");

    // N'ajoutez pas l'en-tête pour les appels de 'login', 'register' ou 'refresh'
    if (
      token &&
      !config.url?.includes("/auth/login") &&
      !config.url?.includes("/auth/refresh-token")
    ) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 2. Ajout de l'en-tête Accept-Language
    const currentLanguage = window.localStorage.getItem("i18nextLng");
    if (currentLanguage) {
      // 💡 Ajout de l'en-tête Accept-Language en utilisant la langue de i18next
      config.headers["Accept-Language"] = currentLanguage;
    }
    // Pour l'intercepteur de réponse, l'Authorization doit être géré manuellement pour les requêtes sensibles comme `getProfile`
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default apiClient;
